import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  XCircle,
  Pause,
  Eye,
  EyeOff,
  Calendar,
} from "lucide-react";
import type { StatusType } from "./status-badge";

/**
 * Configuration for each status
 */
interface StatusConfig {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  className: string;
  icon: React.ReactNode;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  // Publish status
  published: {
    label: "Опубликовано",
    variant: "outline",
    className: "bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20",
    icon: <Eye className="h-3 w-3" />,
  },
  draft: {
    label: "Черновик",
    variant: "outline",
    className: "bg-muted text-muted-foreground border-border hover:bg-muted",
    icon: <EyeOff className="h-3 w-3" />,
  },

  // Active status
  active: {
    label: "Активен",
    variant: "outline",
    className: "bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  inactive: {
    label: "Неактивен",
    variant: "outline",
    className: "bg-muted text-muted-foreground border-border hover:bg-muted",
    icon: <Circle className="h-3 w-3" />,
  },

  // Expiry status
  expired: {
    label: "Истёк",
    variant: "outline",
    className: "bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20",
    icon: <XCircle className="h-3 w-3" />,
  },
  scheduled: {
    label: "Запланировано",
    variant: "outline",
    className: "bg-blue-500/10 text-blue-500 border-blue-500/20 hover:bg-blue-500/20",
    icon: <Calendar className="h-3 w-3" />,
  },

  // Request status
  pending: {
    label: "Ожидает",
    variant: "outline",
    className: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/20",
    icon: <Clock className="h-3 w-3" />,
  },
  approved: {
    label: "Одобрено",
    variant: "outline",
    className: "bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  rejected: {
    label: "Отклонено",
    variant: "outline",
    className: "bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20",
    icon: <XCircle className="h-3 w-3" />,
  },

  // Trigger status
  paused: {
    label: "Приостановлен",
    variant: "outline",
    className: "bg-orange-500/10 text-orange-500 border-orange-500/20 hover:bg-orange-500/20",
    icon: <Pause className="h-3 w-3" />,
  },
  disabled: {
    label: "Отключён",
    variant: "outline",
    className: "bg-muted text-muted-foreground border-border hover:bg-muted",
    icon: <AlertCircle className="h-3 w-3" />,
  },

  // Subscription status
  cancelled: {
    label: "Отменена",
    variant: "outline",
    className: "bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20",
    icon: <XCircle className="h-3 w-3" />,
  },
};

/**
 * Props for StatusBadge component
 */
interface StatusBadgeProps {
  status: StatusType;
  showIcon?: boolean;
  size?: "sm" | "default";
  className?: string;
  customLabel?: string;
}

/**
 * StatusBadge component
 *
 * Displays a status badge with icon and label based on the status type.
 * Supports various status types: publish, active, expiry, request, trigger, subscription.
 */
export function StatusBadge({
  status,
  showIcon = true,
  size = "default",
  className,
  customLabel,
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];

  if (!config) {
    return (
      <Badge variant="outline" className={className}>
        {status}
      </Badge>
    );
  }

  const label = customLabel ?? config.label;

  return (
    <Badge
      variant={config.variant}
      className={cn(
        "gap-1 font-medium",
        config.className,
        size === "sm" && "text-xs px-2 py-0",
        className
      )}
    >
      {showIcon && config.icon}
      {label}
    </Badge>
  );
}
